import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Clock, RefreshCw, Loader2, Calendar, CheckCircle2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

function formatIso(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function SchedulerStatusPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingMonthly, setSendingMonthly] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/scheduler/runs?limit=5");
      setData(data);
    } catch { /* silent for non-admins */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin) fetchStatus(); else setLoading(false); }, [isAdmin, fetchStatus]);

  if (!isAdmin) return null;

  const handleRunNow = async () => {
    setRefreshing(true);
    toast.info("AI price refresh started. This may take a minute...");
    try {
      const { data: result } = await api.post("/market/refresh-prices");
      toast.success(`Refresh complete: ${result.updated}/${result.total} releases updated in ${result.duration_seconds}s`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSendTestDigest = async () => {
    setSendingDigest(true);
    toast.info("Sending test digest email...");
    try {
      const { data: result } = await api.post("/admin/digest/send-test");
      if (result.status === "sent") {
        toast.success(`Digest sent to ${result.recipient}`);
      } else if (result.status === "skipped") {
        toast.error("Digest skipped — RESEND_API_KEY or DIGEST_RECIPIENT missing");
      } else {
        toast.error(`Digest failed: ${result.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSendMonthly = async () => {
    setSendingMonthly(true);
    toast.info("Sending monthly highlights email...");
    try {
      const { data: result } = await api.post("/admin/digest/send-monthly-test");
      if (result.status === "sent") {
        toast.success(`Monthly highlights sent to ${result.recipient}`);
      } else if (result.status === "skipped") {
        toast.error("Monthly digest skipped — email credentials missing");
      } else {
        toast.error(`Monthly digest failed: ${result.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Monthly digest failed");
    } finally {
      setSendingMonthly(false);
    }
  };

  if (loading) return <div className="h-32 bg-zinc-900/40 rounded-xl animate-pulse" />;

  const runs = data?.runs || [];
  const nextRun = data?.next_run_at;

  return (
    <div data-testid="scheduler-status-panel" className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/20 text-sky-400">
            <Clock className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold text-zinc-100">Scheduled Digests</h3>
            <p className="text-[11px] text-zinc-500">Weekly price refresh (Mon 03:00 UTC) &middot; Monthly highlights (1st @ 04:00 UTC)</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
        <Button
          data-testid="run-market-refresh-now"
          onClick={handleRunNow}
          disabled={refreshing}
          size="sm"
          className="bg-rose-600 hover:bg-rose-500 text-white"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Run Now
        </Button>
        <Button
          data-testid="send-test-digest"
          onClick={handleSendTestDigest}
          disabled={sendingDigest}
          size="sm"
          variant="outline"
          className="bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-zinc-100"
        >
          {sendingDigest ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
          Send Test Digest
        </Button>
        <Button
          data-testid="send-monthly-digest"
          onClick={handleSendMonthly}
          disabled={sendingMonthly}
          size="sm"
          variant="outline"
          className="bg-zinc-900 border-amber-500/30 text-amber-400 hover:text-amber-300"
        >
          {sendingMonthly ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Send Monthly
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-zinc-950/40 border border-white/5 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 uppercase tracking-wider">
            <Calendar className="w-3 h-3" /> Next Weekly Refresh
          </div>
          <p className="text-sm text-zinc-200 font-mono mt-1">{formatIso(nextRun)}</p>
        </div>
        <div className="bg-zinc-950/40 border border-amber-500/15 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] text-amber-500/80 uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> Next Monthly Highlights
          </div>
          <p className="text-sm text-zinc-200 font-mono mt-1">{formatIso(data?.next_monthly_at)}</p>
        </div>
        <div className="bg-zinc-950/40 border border-white/5 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 uppercase tracking-wider">
            <CheckCircle2 className="w-3 h-3" /> Last Refresh Run
          </div>
          {runs.length > 0 ? (
            <p className="text-sm text-zinc-200 font-mono mt-1">
              {formatIso(runs[0].finished_at)} &middot; <span className="text-emerald-400">{runs[0].updated}/{runs[0].total}</span>
            </p>
          ) : (
            <p className="text-sm text-zinc-500 mt-1">Never run</p>
          )}
        </div>
      </div>

      {runs.length > 0 && (
        <div className="pt-2 border-t border-zinc-800/50">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Recent Runs</p>
          <div className="space-y-1.5">
            {runs.slice(0, 5).map((r) => (
              <div key={r.run_id} className="flex items-center justify-between text-[11px] text-zinc-400 font-mono gap-2">
                <span className="truncate">{formatIso(r.started_at)}</span>
                <span className="text-zinc-600">{r.trigger}</span>
                <span className="text-emerald-400 flex-shrink-0">{r.updated}/{r.total}</span>
                <span className="text-zinc-600 flex-shrink-0">{r.duration_seconds}s</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
